// src/App.js
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useEffect, useRef, lazy, Suspense } from "react";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import ScrollToTopOnRoute from "components/ScrollToTopOnRoute/ScrollToTopOnRoute";
import Home from "./Pages/Etusivu/Etusivu";
import Paavalikko from "./components/Paavalikko/Paavalikko";
import Footer from "./components/Footer/Footer";
import { PageOverlayLoading } from "./components/Loading/Spinner.jsx";
import { ThemeProvider } from "./context/ThemeContext";
import ScrollToTop from "./components/utils/ScrollToTop";
import "aos/dist/aos.css";
import emailjs from "emailjs-com";
import AppToast from "./ui/AppToast";
import "react-toastify/dist/ReactToastify.css";
import TeamNoteModal from "./components/TeamNoteModal/TeamNoteModal";
// Auth
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute.jsx";
import FloatingDashboardCTA from "./components/FloatingDashboardCTA/FloatingDashboardCTA.jsx";
import { trackPageView } from "./utils/analytics";
// Evästebanneri lazyksi, ettei se tule mukaan etusivun ensimmäiseen JS-bundleen.
const CookieConsent = lazy(() => import("./components/Cookies/CookieConsent"));
// Landingit lazyinä
const DjLanding = lazy(() => import("./Pages/Landing/DjLanding.jsx"));
const EmbedLanding = lazy(() => import("./Pages/Landing/EmbedLanding.jsx"));
const ParanoidiLanding = lazy(() => import("./Pages/Landing/ParanoidiLanding.jsx"));
const VerkkosivutLanding = lazy(() =>
  import("./Pages/Landing/VerkkosivutLanding.jsx")
);
const ValokuvaajaOuluLanding = lazy(() =>
  import("./Pages/Landing/ValokuvaajaOuluLanding.jsx")
);
const TaikuriOuluLanding = lazy(() =>
  import("./Pages/Landing/TaikuriOuluLanding.jsx")
);
const JuontajaOuluLanding = lazy(() =>
  import("./Pages/Landing/JuontajaOuluLanding.jsx")
);
const StandupKoomikkoOuluLanding = lazy(() =>
  import("./Pages/Landing/StandupKoomikkoOuluLanding.jsx")
);
const SooloartistiOuluLanding = lazy(() =>
  import("./Pages/Landing/SooloartistiOuluLanding.jsx")
);
const BilebandiLanding = lazy(() => import("./Pages/Landing/BilebandiLanding.jsx"));
const TrubaduuriLanding = lazy(() => import("./Pages/Landing/TrubaduuriLanding.jsx"));
const DuoLanding = lazy(() => import("./Pages/Landing/DuoLanding.jsx"));
const SooloartistiLanding = lazy(() => import("./Pages/Landing/SooloartistiLanding.jsx"));
const TaikuriLanding = lazy(() => import("./Pages/Landing/TaikuriLanding.jsx"));
const ValokuvaajaLanding = lazy(() => import("./Pages/Landing/ValokuvaajaLanding.jsx"));
const JuontajaLanding = lazy(() => import("./Pages/Landing/JuontajaLanding.jsx"));
const StandupKoomikkoLanding = lazy(() => import("./Pages/Landing/StandupKoomikkoLanding.jsx"));
const ValokuvaajaUusimaaLanding = lazy(() => import("./Pages/Landing/ValokuvaajaUusimaaLanding.jsx"));
const BilebandiKeravaLanding = lazy(() => import("./Pages/Landing/BilebandiKeravaLanding.jsx"));
const BilebandiTuusulaLanding = lazy(() => import("./Pages/Landing/BilebandiTuusulaLanding.jsx"));
const BilebandiVantaaLanding = lazy(() => import("./Pages/Landing/BilebandiVantaaLanding.jsx"));
const BilebandiJarvenpaaLanding = lazy(() => import("./Pages/Landing/BilebandiJarvenpaaLanding.jsx"));
const BilebandiEspooLanding = lazy(() => import("./Pages/Landing/BilebandiEspooLanding.jsx"));
const ValokuvaajaPirkanmaaLanding = lazy(() => import("./Pages/Landing/ValokuvaajaPirkanmaaLanding.jsx"));
const ValokuvaajaVarsinaisSuomiLanding = lazy(() => import("./Pages/Landing/ValokuvaajaVarsinaisSuomiLanding.jsx"));
const ValokuvaajaKantaHameLanding = lazy(() => import("./Pages/Landing/ValokuvaajaKantaHameLanding.jsx"));
const TaikuriUusimaaLanding = lazy(() => import("./Pages/Landing/TaikuriUusimaaLanding.jsx"));
const TaikuriHelsinkiLanding = lazy(() => import("./Pages/Landing/TaikuriHelsinkiLanding.jsx"));
const TaikuriPirkanmaaLanding = lazy(() => import("./Pages/Landing/TaikuriPirkanmaaLanding.jsx"));
const TaikuriVarsinaisSuomiLanding = lazy(() => import("./Pages/Landing/TaikuriVarsinaisSuomiLanding.jsx"));
const TaikuriSatakuntaLanding = lazy(() => import("./Pages/Landing/TaikuriSatakuntaLanding.jsx"));
const TaikuriKantaHameLanding = lazy(() => import("./Pages/Landing/TaikuriKantaHameLanding.jsx"));
const TaikuriPaijatHameLanding = lazy(() => import("./Pages/Landing/TaikuriPaijatHameLanding.jsx"));
const DuoKantaHameLanding = lazy(() => import("./Pages/Landing/DuoKantaHameLanding.jsx"));
const DuoVarsinaisSuomiLanding = lazy(() => import("./Pages/Landing/DuoVarsinaisSuomiLanding.jsx"));
const DuoPirkanmaaLanding = lazy(() => import("./Pages/Landing/DuoPirkanmaaLanding.jsx"));
const DuoOuluLanding = lazy(() => import("./Pages/Landing/DuoOuluLanding.jsx"));
const DuoPaijatHameLanding = lazy(() => import("./Pages/Landing/DuoPaijatHameLanding.jsx"));
const DuoSatakuntaLanding = lazy(() => import("./Pages/Landing/DuoSatakuntaLanding.jsx"));
const DuoKymenlaaksoLanding = lazy(() => import("./Pages/Landing/DuoKymenlaaksoLanding.jsx"));
const SooloartistiUusimaaLanding = lazy(() => import("./Pages/Landing/SooloartistiUusimaaLanding.jsx"));
const SooloartistiPirkanmaaLanding = lazy(() => import("./Pages/Landing/SooloartistiPirkanmaaLanding.jsx"));
const SooloartistiVarsinaisSuomiLanding = lazy(() => import("./Pages/Landing/SooloartistiVarsinaisSuomiLanding.jsx"));
const SooloartistiKantaHameLanding = lazy(() => import("./Pages/Landing/SooloartistiKantaHameLanding.jsx"));
const SooloartistiPaijatHameLanding = lazy(() => import("./Pages/Landing/SooloartistiPaijatHameLanding.jsx"));
const SooloartistiSatakuntaLanding = lazy(() => import("./Pages/Landing/SooloartistiSatakuntaLanding.jsx"));
const SooloartistiKymenlaaksoLanding = lazy(() => import("./Pages/Landing/SooloartistiKymenlaaksoLanding.jsx"));
const BilebandiUusimaaLanding = lazy(() => import("./Pages/Landing/BilebandiUusimaaLanding.jsx"));
const TrubaduuriTampereLanding = lazy(() => import("./Pages/Landing/TrubaduuriTampereLanding.jsx"));
const TrubaduuriHelsinkiLanding = lazy(() => import("./Pages/Landing/TrubaduuriHelsinkiLanding.jsx"));
const TrubaduuriTurkuLanding = lazy(() => import("./Pages/Landing/TrubaduuriTurkuLanding.jsx"));
const TrubaduuriOuluLanding = lazy(() => import("./Pages/Landing/TrubaduuriOuluLanding.jsx"));
const TrubaduuriUusimaaLanding = lazy(() => import("./Pages/Landing/TrubaduuriUusimaaLanding.jsx"));
const DjUusimaaLanding = lazy(() => import("./Pages/Landing/DjUusimaaLanding.jsx"));
const DuoUusimaaLanding = lazy(() => import("./Pages/Landing/DuoUusimaaLanding.jsx"));
const DuoHelsinkiLanding = lazy(() => import("./Pages/Landing/DuoHelsinkiLanding.jsx"));
const BilebandiPirkanmaaLanding = lazy(() => import("./Pages/Landing/BilebandiPirkanmaaLanding.jsx"));
const BilebandiOuluLanding = lazy(() => import("./Pages/Landing/BilebandiOuluLanding.jsx"));
const TrubaduuriPirkanmaaLanding = lazy(() => import("./Pages/Landing/TrubaduuriPirkanmaaLanding.jsx"));
const DjPirkanmaaLanding = lazy(() => import("./Pages/Landing/DjPirkanmaaLanding.jsx"));
const DjOuluLanding = lazy(() => import("./Pages/Landing/DjOuluLanding.jsx"));
const BilebandiPaijatHameLanding = lazy(() => import("./Pages/Landing/BilebandiPaijatHameLanding.jsx"));
const TrubaduuriPaijatHameLanding = lazy(() => import("./Pages/Landing/TrubaduuriPaijatHameLanding.jsx"));
const DjPaijatHameLanding = lazy(() => import("./Pages/Landing/DjPaijatHameLanding.jsx"));
const BilebandiVarsinaisSuomiLanding = lazy(() => import("./Pages/Landing/BilebandiVarsinaisSuomiLanding.jsx"));
const BilebandiHelsinkiLanding = lazy(() => import("./Pages/Landing/BilebandiHelsinkiLanding.jsx"));
const TrubaduuriVarsinaisSuomiLanding = lazy(() => import("./Pages/Landing/TrubaduuriVarsinaisSuomiLanding.jsx"));
const DjVarsinaisSuomiLanding = lazy(() => import("./Pages/Landing/DjVarsinaisSuomiLanding.jsx"));
// Muut reittikomponentit lazyinä, jotta etusivun ensimmäinen bundle kevenee
const BandProfile = lazy(() => import("./components/Esiintyjaprofiili/Esiintyjaprofiili"));
const CompanyProfile = lazy(() => import("./components/Yritysprofiili/CompanyProfile.jsx"));
const TietoaMeista = lazy(() => import("./Pages/Tietomeista/TietoaMeista"));
const UKK = lazy(() => import("./Pages/UKK/UKK.jsx"));
const EmbedPage = lazy(() => import("./Pages/Embed/EmbedPage.jsx"));
const UKKArtists = lazy(() => import("./Pages/UKK/UKKArtists.jsx"));
const OtaYhteytta = lazy(() => import("./Pages/OtaYhteytta/OtaYhteytta.jsx"));
const Tietosuoja = lazy(() => import("./Pages/Tietosuoja/Tietosuoja.jsx"));
const PerformersPage = lazy(() => import("./Pages/Performers/PerformersPage"));
const Kayttoehdot = lazy(() => import("./Pages/Kayttoehdot/Kayttoehdot.jsx"));
const Blogi = lazy(() => import("./Pages/Blogi/Blogi.jsx"));
const SingleBlog = lazy(() => import("./Pages/SingleBlog/SingleBlog.jsx"));
const LiityEsiintyjaksi = lazy(() => import("./Pages/LiityEsiintyjaksi/LiityEsiintyjaksi.jsx"));
const YhteistyoPage = lazy(() => import("./Pages/Yhteistyo/Yhteistyo.jsx"));
const NotFound = lazy(() => import("./Pages/NotFound/NotFound.jsx"));
const PerformerResult = lazy(() => import("./Pages/PerformerResult/PerformerResult.jsx"));
const BookingResult = lazy(() => import("./Pages/BookingResult/BookingResult.jsx"));
const Register = lazy(() => import("./Pages/Auth/Register.jsx"));
const Login = lazy(() => import("./Pages/Auth/Login.jsx"));
const OnboardingGate = lazy(() => import("./Pages/Onboarding/OnboardingGate.jsx"));
const Forgot = lazy(() => import("./Pages/Auth/Forgot.jsx"));
const Reset = lazy(() => import("./Pages/Auth/Reset.jsx"));
const VerifyEmail = lazy(() => import("./Pages/Auth/VerifyEmail.jsx"));
const OAuthFinish = lazy(() => import("./Pages/Auth/OAuthFinish.jsx"));
const ProfileReviewResult = lazy(() => import("./Pages/ProfileReviewResult/ProfileReviewResult"));
const SetupWizardAdd = lazy(() => import("./Pages/Setup/SetupWizardAdd.jsx"));
const Start = lazy(() => import("./Pages/Start/Start.jsx"));
const Dashboard = lazy(() => import("./Pages/Dashboard/Dashboard.jsx"));
const PublicProfilesPage = lazy(() => import("./Pages/PublicProfiles/PublicProfilesPage.jsx"));
if (process.env.NODE_ENV === "development") {
  window.emailjs = emailjs;
  emailjs.init(process.env.REACT_APP_EMAILJS_USER_ID?.trim());
}
// Create React App -ympäristöliput
const IS_PROD =
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV === "production";
const IS_DEV = !IS_PROD;
// --- Meta Pixel: lähetä PageView jokaisessa reitinvaihdossa (SPA)
function FbqPageView() {
  const loc = useLocation();
  const first = useRef(true);
  useEffect(() => {
    // Vältä tupla: base-pikseli lähetti jo ensimmäisen PageView'n
    if (first.current) {
      first.current = false;
      return;
    }
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [loc.pathname, loc.search]);
  return null;
}
// --- GA4: manuaalinen page_view jokaisesta reitinvaihdosta
function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    // Käytetään virtuaalisena polkuna pathname + search (ei hashia)
    const path = location.pathname + location.search;
    const title =
      typeof document !== "undefined" ? document.title : undefined;
    try {
      trackPageView(path, title);
    } catch (err) {
      if (IS_DEV) {
        console.warn("trackPageView failed", err);
      }
    }
  }, [location.pathname, location.search]);
  return null;
}
// Alias-komponentti: /login -> /kirjaudu (säilyttää ?queryn)
function LoginAlias() {
  const location = useLocation();
  return <Navigate to={`/kirjaudu${location.search || ""}`} replace />;
}
// Alias: /preview/profiili/:slug -> /profiili/:slug?preview=1&...
function PreviewProfileAlias() {
  const { slug } = useParams();
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search || "");
  qs.set("preview", "1");
  return <Navigate to={`/profiili/${slug}?${qs.toString()}`} replace />;
}
// Alias: /preview/esiintyja/:slug -> /profiili/:slug?preview=1&...
function PreviewAlias() {
  const { slug } = useParams();
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search || "");
  qs.set("preview", "1");
  return <Navigate to={`/profiili/${slug}?${qs.toString()}`} replace />;
}
// Alias: /esiintyja/:slug -> /profiili/:slug (säilyttää queryt)
function OldPerformerProfileAlias() {
  const { slug } = useParams();
  const loc = useLocation();
  return <Navigate to={`/profiili/${slug}${loc.search || ""}`} replace />;
}
// Alias: vanhat tulosreitit -> uudet profiili-tulosreitit (säilyttää queryt)
function OldPerformerResultAlias({ to }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search || ""}`} replace />;
}
// Alias: vanha UKK-reitti -> uusi neutraalimpi profiili-UKK
function OldPerformerFaqAlias() {
  const loc = useLocation();
  return <Navigate to={`/ukk-profiileille${loc.search || ""}`} replace />;
}
// Yleiskäyttöinen alias, joka säilyttää queryt
function RedirectPreserveQuery({ to }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search || ""}`} replace />;
}
// Hinta-alias: /bilebandi-hinnat -> /bilebandi-juhliin#hinnat (säilyttää ?queryn)
function PriceAlias({ pathname }) {
  const loc = useLocation();
  return <Navigate to={`${pathname}${loc.search || ""}#hinnat`} replace />;
}
// Kevyt apu: ohjaa suoraan hakunäkymään esivalituilla parametreilla
function RegionRedirect({ type, region, book = false }) {
  const qs = new URLSearchParams();
  qs.set("type", String(type));
  qs.set("maakunta", String(region));
  if (book) qs.set("book", "1");
  qs.set("clear", "1");
  return <Navigate to={`/?${qs.toString()}#tulokset`} replace />;
}
function RouterContent() {
  const location = useLocation();
  const isEmbedRoute = location.pathname.startsWith("/embed/");
  return (
    <>
      <ScrollToTopOnRoute />
      <ScrollToTop />
      <FbqPageView />
      <AnalyticsTracker />
      <AuthProvider>
        {!isEmbedRoute && <Paavalikko />}
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/esiintyjat-ja-palvelut" element={<PublicProfilesPage />} />
            <Route path="/esiintyjat-ja-palvelut/" element={<PublicProfilesPage />} />
            {process.env.REACT_APP_ENABLE_EMBED === "true" && (
              <Route path="/embed/:slug" element={<EmbedPage />} />
            )}
            {/* Hinta-aliasreitit: ei uusia sivuja, ohjaa olemassa olevaan ländäriin #hinnat */}
            <Route path="/bilebandi-hinnat/*" element={<PriceAlias pathname="/bilebandi-juhliin" />} />
            <Route path="/dj-hinnat/*" element={<PriceAlias pathname="/dj-juhliin" />} />
            <Route path="/duo-hinnat/*" element={<PriceAlias pathname="/duo-juhliin" />} />
            <Route path="/taikuri-hinnat/*" element={<PriceAlias pathname="/taikuri-juhliin" />} />
            <Route path="/trubaduuri-hinnat/*" element={<PriceAlias pathname="/trubaduuri-juhliin" />} />
            <Route path="/sooloartisti-hinnat/*" element={<PriceAlias pathname="/sooloartisti-juhliin" />} />
            {/* Landing - KOKO SUOMI */}
            <Route path="/dj-juhliin/*" element={<DjLanding />} />
            <Route path="/buukkeri-embed/*" element={<EmbedLanding />} />
            <Route path="/paranoidi/*" element={<ParanoidiLanding />} />
            <Route
              path="/verkkosivut-palveluntarjoajalle/*"
              element={<VerkkosivutLanding />}
            />
            <Route path="/bilebandi-juhliin/*" element={<BilebandiLanding />} />
            <Route path="/trubaduuri-juhliin/*" element={<TrubaduuriLanding />} />
            <Route path="/duo-juhliin/*" element={<DuoLanding />} />
            <Route path="/sooloartisti-juhliin/*" element={<SooloartistiLanding />} />
            <Route path="/valokuvaaja-juhliin/*" element={<ValokuvaajaLanding />} />
            <Route path="/taikuri-juhliin/*" element={<TaikuriLanding />} />
            <Route path="/juontaja-juhliin/*" element={<JuontajaLanding />} />
            <Route path="/standup-koomikko-juhliin/*" element={<StandupKoomikkoLanding />} />
            {/* ---- Maakuntaländärit: Uusimaa ---- */}
            <Route path="/bilebandi-uusimaa/*" element={<BilebandiUusimaaLanding />} />
            <Route path="/trubaduuri-helsinki/*" element={<TrubaduuriHelsinkiLanding />} />
            <Route path="/trubaduuri-turku/*" element={<TrubaduuriTurkuLanding />} />
            <Route path="/bilebandi-helsinki/*" element={<BilebandiHelsinkiLanding />} />
            <Route path="/bilebandi-espoo/*" element={<BilebandiEspooLanding />} />
            <Route path="/dj-uusimaa/*" element={<DjUusimaaLanding />} />
            <Route path="/bilebandi-kerava/*" element={<BilebandiKeravaLanding />} />
            <Route path="/bilebandi-jarvenpaa/*" element={<BilebandiJarvenpaaLanding />} />
            <Route path="/bilebandi-tuusula/*" element={<BilebandiTuusulaLanding />} />
            <Route path="/bilebandi-vantaa/*" element={<BilebandiVantaaLanding />} />
            <Route path="/trubaduuri-uusimaa/*" element={<TrubaduuriUusimaaLanding />} />
            <Route path="/duo-uusimaa/*" element={<DuoUusimaaLanding />} />
            <Route path="/duo-helsinki/*" element={<DuoHelsinkiLanding />} />
            <Route path="/valokuvaaja-uusimaa/*" element={<ValokuvaajaUusimaaLanding />} />
            <Route path="/sooloartisti-uusimaa/*" element={<SooloartistiUusimaaLanding />} />
            <Route path="/taikuri-uusimaa/*" element={<TaikuriUusimaaLanding />} />
            <Route path="/taikuri-helsinki/*" element={<TaikuriHelsinkiLanding />} />
            {/* ---- Pirkanmaa: omat React-ländärit ---- */}
            <Route path="/bilebandi-pirkanmaa/*" element={<BilebandiPirkanmaaLanding />} />
            <Route path="/dj-pirkanmaa/*" element={<DjPirkanmaaLanding />} />
            <Route path="/trubaduuri-pirkanmaa/*" element={<TrubaduuriPirkanmaaLanding />} />
            <Route path="/trubaduuri-tampere/*" element={<TrubaduuriTampereLanding />} />
            <Route path="/duo-pirkanmaa/*" element={<DuoPirkanmaaLanding />} />
            <Route path="/duo-paijat-hame/*" element={<DuoPaijatHameLanding />} />
            <Route path="/sooloartisti-pirkanmaa/*" element={<SooloartistiPirkanmaaLanding />} />
            <Route path="/duo-varsinais-suomi/*" element={<DuoVarsinaisSuomiLanding />} />
            <Route path="/duo-kanta-hame/*" element={<DuoKantaHameLanding />} />
            <Route path="/sooloartisti-kanta-hame/*" element={<SooloartistiKantaHameLanding />} />
            <Route path="/valokuvaaja-pirkanmaa/*" element={<ValokuvaajaPirkanmaaLanding />} />
            <Route path="/taikuri-pirkanmaa/*" element={<TaikuriPirkanmaaLanding />} />
            {/* ---- Päijät-Häme: omat React-ländärit ---- */}
            <Route path="/bilebandi-paijat-hame/*" element={<BilebandiPaijatHameLanding />} />
            <Route path="/dj-paijat-hame/*" element={<DjPaijatHameLanding />} />
            <Route path="/trubaduuri-paijat-hame/*" element={<TrubaduuriPaijatHameLanding />} />
            <Route path="/taikuri-paijat-hame/*" element={<TaikuriPaijatHameLanding />} />
            <Route path="/sooloartisti-paijat-hame/*" element={<SooloartistiPaijatHameLanding />} />
            {/* ---- Varsinais-Suomi: omat React-ländärit ---- */}
            <Route path="/bilebandi-varsinais-suomi/*" element={<BilebandiVarsinaisSuomiLanding />} />
            <Route path="/dj-varsinais-suomi/*" element={<DjVarsinaisSuomiLanding />} />
            <Route path="/trubaduuri-varsinais-suomi/*" element={<TrubaduuriVarsinaisSuomiLanding />} />
            <Route path="/valokuvaaja-varsinais-suomi/*" element={<ValokuvaajaVarsinaisSuomiLanding />} />
            <Route path="/sooloartisti-varsinais-suomi/*" element={<SooloartistiVarsinaisSuomiLanding />} />
            <Route path="/taikuri-varsinais-suomi/*" element={<TaikuriVarsinaisSuomiLanding />} />
            {/* ---- Kanta-Häme: omat React-ländärit ---- */}
            <Route path="/valokuvaaja-kanta-hame/*" element={<ValokuvaajaKantaHameLanding />} />
            <Route path="/taikuri-kanta-hame/*" element={<TaikuriKantaHameLanding />} />
            {/* ---- Satakunta: omat React-ländärit ---- */}
            <Route path="/duo-satakunta/*" element={<DuoSatakuntaLanding />} />
            <Route path="/sooloartisti-satakunta/*" element={<SooloartistiSatakuntaLanding />} />
            <Route path="/taikuri-satakunta/*" element={<TaikuriSatakuntaLanding />} />
            {/* ---- Kymenlaakso: omat React-ländärit ---- */}
            <Route path="/duo-kymenlaakso/*" element={<DuoKymenlaaksoLanding />} />
            <Route path="/sooloartisti-kymenlaakso/*" element={<SooloartistiKymenlaaksoLanding />} />
            {/* ---- Pohjois-Pohjanmaa ---- */}
            <Route path="/bilebandi-oulu/*" element={<BilebandiOuluLanding />} />
            <Route path="/trubaduuri-oulu/*" element={<TrubaduuriOuluLanding />} />
            <Route path="/dj-oulu/*" element={<DjOuluLanding />} />
            <Route path="/duo-oulu/*" element={<DuoOuluLanding />} />
            <Route path="/valokuvaaja-oulu/*" element={<ValokuvaajaOuluLanding />} />
            <Route path="/taikuri-oulu/*" element={<TaikuriOuluLanding />} />
            <Route path="/juontaja-oulu/*" element={<JuontajaOuluLanding />} />
            <Route path="/standup-koomikko-oulu/*" element={<StandupKoomikkoOuluLanding />} />
            <Route path="/sooloartisti-oulu/*" element={<SooloartistiOuluLanding />} />
            {/* Maakuntaländärit – toistaiseksi suora ohjaus hakuun */}
            <Route path="/bilebandi-pohjois-pohjanmaa/*" element={<RegionRedirect type="bilebandi" region="pohjois-pohjanmaa" />} />
            <Route path="/dj-pohjois-pohjanmaa/*" element={<RegionRedirect type="dj" region="pohjois-pohjanmaa" />} />
            <Route path="/trubaduuri-pohjois-pohjanmaa/*" element={<RegionRedirect type="trubaduuri" region="pohjois-pohjanmaa" />} />
            {/* Keski-Suomi – toistaiseksi suora ohjaus hakuun */}
            <Route path="/bilebandi-keski-suomi/*" element={<RegionRedirect type="bilebandi" region="keski-suomi" />} />
            <Route path="/dj-keski-suomi/*" element={<RegionRedirect type="dj" region="keski-suomi" />} />
            <Route path="/trubaduuri-keski-suomi/*" element={<RegionRedirect type="trubaduuri" region="keski-suomi" />} />
            {IS_DEV && (
              <>
                <Route path="/preview/profiili/:slug" element={<PreviewProfileAlias />} />
                <Route path="/preview/esiintyja/:slug" element={<PreviewAlias />} />
              </>
            )}
            <Route path="/yritys/:slug" element={<CompanyProfile />} />
            <Route path="/blogi" element={<Blogi />} />
            <Route path="/blogi/:slug" element={<SingleBlog />} />
            <Route path="/tietoameista" element={<TietoaMeista />} />
            <Route path="/yhteistyo" element={<YhteistyoPage />} />
            <Route path="/useinkysyttya" element={<UKK />} />
            <Route path="/ukk-profiileille" element={<UKKArtists />} />
            <Route path="/ukk-esiintyjille" element={<OldPerformerFaqAlias />} />
            <Route path="/otayhteytta" element={<OtaYhteytta />} />
            <Route path="/ukk" element={<Navigate to="/useinkysyttya" replace />} />
            <Route path="/backstage/*" element={<PerformersPage />} />
            <Route path="/kayttoehdot" element={<Kayttoehdot />} />
            <Route path="/tietosuoja" element={<Tietosuoja />} />
            <Route path="/liity-buukkeriin" element={<LiityEsiintyjaksi />} />
            <Route path="/varaus/kiitos/*" element={<BookingResult />} />
            <Route path="/varaus/peruttu/*" element={<BookingResult />} />
            <Route path="/kiitos/*" element={<BookingResult />} />
            <Route path="/peruttu/*" element={<BookingResult />} />
            <Route path="/profiili/kiitos/*" element={<PerformerResult variant="accepted" />} />
            <Route path="/profiili/hylatty/*" element={<PerformerResult variant="rejected" />} />
            <Route path="/esiintyja/kiitos/*" element={<OldPerformerResultAlias to="/profiili/kiitos" />} />
            <Route path="/esiintyja/hylatty/*" element={<OldPerformerResultAlias to="/profiili/hylatty" />} />
            <Route path="/varaus__trashed/kiitos/*" element={<RedirectPreserveQuery to="/varaus/kiitos" />} />
            <Route path="/varaus__trashed/peruttu/*" element={<RedirectPreserveQuery to="/varaus/peruttu" />} />
            <Route path="/rekisteroidy" element={<Register />} />
            <Route path="/login" element={<LoginAlias />} />
            <Route path="/kirjaudu" element={<Login />} />
            <Route path="/onboarding" element={<OnboardingGate />} />
            <Route path="/setup/add" element={<SetupWizardAdd />} />
            <Route path="/setup" element={<Navigate to="/dashboard?band=user" replace />} />
            <Route path="/setup/*" element={<Navigate to="/dashboard?band=user" replace />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/start" element={<Start />} />
            <Route path="/oauth/google/callback" element={<OAuthFinish provider="google" />} />
            <Route path="/profiili/hyvaksytty" element={<ProfileReviewResult />} />
            <Route path="/profiili/virhe" element={<ProfileReviewResult />} />
            <Route
              path="/profiili/:slug"
              element={
                <Suspense
                  fallback={
                    <PageOverlayLoading label="Ladataan esiintyjän tietoja…" />
                  }
                >
                  <BandProfile />
                </Suspense>
              }
            />
            <Route path="/esiintyja/:slug" element={<OldPerformerProfileAlias />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        {!isEmbedRoute && (
          <>
            <FloatingDashboardCTA />
            <Footer />
            <Suspense fallback={null}>
              <CookieConsent />
            </Suspense>
            <TeamNoteModal />
          </>
        )}
      </AuthProvider>
    </>
  );
}
function AppShellContent() {
  const location = useLocation();
  const isEmbedRoute = location.pathname.startsWith("/embed/");
  if (isEmbedRoute) {
    return <RouterContent />;
  }
  return (
    <div className="min-h-screen overflow-x-hidden relative bg-neon-gradient dark:bg-bile-dark">
      {/*&#x20;
        Talleta esikatselutoken kerran kun sovellus käynnistyy (ja kun käyttäjä
        saapuu esikatselulinkistä). Ei vaikuta muihin reitteihin.
      */}
      <div className="relative z-10">
        <RouterContent />
      </div>
    </div>
  );
}
function AppShell() {
  return (
    <BrowserRouter>
      <AppShellContent />
    </BrowserRouter>
  );
}
export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AppShell />
        <AppToast />
      </ThemeProvider>
    </HelmetProvider>
  );
}